import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
// Client-side data layer for Orion Pages — the dashboard model. A page is now an Orion-authored HTML shell
// (`pages/<slug>/page.html`) with `<div data-sym-widget="<slug>"></div>` slots; each widget is an HTML
// *fragment* injected into a slot. The server resolves a page into ONE composed HTML document (common-lib's
// `resolveDashboard`) and ships it here; the host renders it as a single sandboxed iframe. There is no grid,
// no containers, and no expand/stack/gallery — Orion authors all layout + interactions inside the page HTML.
//
// Mirrors the fetch conventions of lib/widgets.ts: hit `${API_BASE}/api/...` with credentials; the API routes
// resolve the caller's metasite. orion-web's CLIENT bundle must not import common-lib (its RPC codegen + raw
// TS can't be webpacked for the browser — only the server externalizes it), so the wire shapes live here as a
// client-local contract that matches what the server route returns.


// A built-in app section; the user's own pages are addressed by slug instead (see NavDestination). These
// MIRROR common-lib's NavSection / NavDestination (their string values match exactly) so the client can use
// them without importing common-lib — the wire is JSON, and the server resolves through common-lib's real
// enums, which serialize to these strings.

/** A built-in app section. The user's own Orion pages are addressed by slug instead (see NavDestination). */
export enum NavSection {
  Home = 'home',
  Tasks = 'tasks',
  Pulses = 'pulses',
  Knowledge = 'knowledge',
  Connectors = 'connectors',
  Notifications = 'notifications',
  Agents = 'agents',
  Settings = 'settings',
}

/** Where navigation can go: a built-in section, one of the user's pages by slug, or a workspace (opens
 *  that workspace's home page). Mirrors common-lib's NavDestination. */
export type NavDestination = { section: NavSection } | { page: string } | { workspace: string };

/** A built-in section → its web route, for an `orion.navigate({ section })` from a dashboard (sections are other screens). */
export const SECTION_PATH: Record<NavSection, string> = {
  [NavSection.Home]: '/',
  [NavSection.Tasks]: '/tasks',
  [NavSection.Pulses]: '/pulses',
  [NavSection.Knowledge]: '/knowledge',
  [NavSection.Connectors]: '/connectors',
  [NavSection.Notifications]: '/notifications',
  [NavSection.Agents]: '/agents',
  [NavSection.Settings]: '/settings',
};

/** A page in the switcher list — its name + how many widgets it holds. */
export interface PageSummary {
  slug: string;
  name: string;
  icon?: string;
  count: number;
}

/** One workspace and its own dashboard pages (the genome builds a set per workspace). Mirrors the
 *  mobile "Workspaces" drawer: a named group the user can expand into its pages. */
export interface WorkspaceGroup {
  slug: string;
  name: string;
  icon?: string;
  /** The workspace's home page slug (within the group), or null when it has none. */
  home: string | null;
  pages: PageSummary[];
}

/** The pages index: the home pointer + every global page in display order, plus the per-workspace groups. */
export interface PagesIndex {
  home: string | null;
  pages: PageSummary[];
  /** Per-workspace page groups (empty when the user has no workspace-scoped dashboards). */
  workspaces: WorkspaceGroup[];
}

/** Whether `slug` names one of the user's workspaces (a workspace has its OWN dashboard), as opposed to a
 *  drill-in page of the home. Used to recover a workspace nav that was mis-addressed as a `{ page }`: a
 *  workspace is `{ workspace }`, so a `{ page }` slug that's actually a workspace resolves empty (there's no
 *  such drill-in page on the home) — we send the user to the workspace's dashboard instead of a dead end. */
export function isWorkspaceSlug(index: PagesIndex, slug: string): boolean {
  return index.workspaces.some((workspace) => workspace.slug === slug);
}

// The home index already lists EXACTLY the workspaces that have a dashboard (its `workspaces` groups),
// so dashboard-presence is known SYNCHRONOUSLY from the loaded index — no per-workspace fetch. Returns
// `undefined` while the index is still loading/absent (unknown), and true/false once it's `present`.
// Type-only import of HomepageState — erased at runtime, so this module stays free of React/component deps.
export function workspaceHasDashboard(
  homepage: import('../components/HomepageContext').HomepageState,
  slug: string,
): boolean | undefined {
  if (homepage.status !== 'present') return undefined;
  return isWorkspaceSlug(homepage.index, slug);
}

/** A workspace nav target: the Next `href` (route + query) plus the mount-prefixed `as` URL. */
export interface WorkspaceRoute {
  href: { pathname: string; query: { slug: string } };
  as: string;
}

// The default landing route for a workspace ENTRY point. A workspace with NO dashboard has an empty
// Growth & Goals tab, so its entry lands on "Agents at work" instead; otherwise — it has a dashboard, or
// the index isn't loaded yet (unknown) — it lands on Growth & Goals. Either way the in-screen tab bar
// still reaches the other tab, so this only picks the DEFAULT, never blocks a destination.
export function workspaceEntryRoute(
  homepage: import('../components/HomepageContext').HomepageState,
  slug: string,
  basePath: string,
): WorkspaceRoute {
  const encoded = encodeURIComponent(slug);
  if (workspaceHasDashboard(homepage, slug) === false) {
    return {
      href: { pathname: '/workspace/[slug]/agents', query: { slug } },
      as: `${basePath}/workspace/${encoded}/agents`,
    };
  }
  return {
    href: { pathname: '/workspace/[slug]', query: { slug } },
    as: `${basePath}/workspace/${encoded}`,
  };
}

export async function fetchPagesIndex(): Promise<PagesIndex> {
  const res = await apiFetch(`${API_BASE}/api/pages`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }
  return (await res.json()) as PagesIndex;
}

async function readErrorMessage(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as { error?: string; message?: string };
    return data.error || data.message || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}
